import React from 'react';
import { Modal, Card, Button, Badge, ListGroup, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faClock, faMapMarkerAlt, faUser, faPhone, faUserNurse, faBirthdayCake } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import './ClientDetailModal.css';
import {IClient, ReactData} from "../../utils/FrontendDataTypes";
import {de} from "date-fns/locale";

export interface ClientDetailModalProps {
  show: boolean;
  onHide: () => void;
  client: IClient | null;
  data: ReactData|null,
  onConfirm?: (tomorrow: boolean) => void;
  loading?: boolean;
  showConfirmButton?: boolean;
}

export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({ 
  show, 
  onHide, 
  client, 
  onConfirm,
    data,
  loading = false,
  showConfirmButton = false
}) => {
  if (!client) return null;

  // Zeiten für den Stundenplan extrahieren
  const timeEvents = [];
  if (client.timetable?.beginning_of_the_way_to_school) {
    timeEvents.push({ time: client.timetable.beginning_of_the_way_to_school, label: 'Schulweg beginnt' });
  }
  if (client.timetable?.beginning_school_support) {
    timeEvents.push({ time: client.timetable.beginning_school_support, label: 'Schulbegleitung beginnt' });
  }
  if (client.timetable?.end_school_support) {
    timeEvents.push({ time: client.timetable.end_school_support, label: 'Schulbegleitung endet' });
  }
  if (client.timetable?.end_of_the_way_to_school) {
    timeEvents.push({ time: client.timetable.end_of_the_way_to_school, label: 'Schulweg endet' });
  }

  const enableStandInTodayButton = (() : boolean => {
    if (loading) {
      return false
    }

    // Wenn der Klient heute einen freien Tag hat dann nicht anzeigen
    if (client.additional_information?.today_off) {
      return false
    }

    if (data?.employee_sick_today) {
      return false
    }
    if (data?.is_employee_client_sick_today === false) {
      return false
    }
    if (data?.stand_in_for_client_today !== undefined) {
      return false
    }

    if (
        client.additional_information?.employee_sick_today === true &&
        (
            client.additional_information?.stand_in_today === undefined ||
            client.additional_information?.stand_in_today === 0
        )
    ) {
      return true
    }

    return false

  })()

  const enableStandInNextWorkDayButton = (() : boolean => {
    if (loading) {
      return false
    }

    // Wenn der Klient am nächsten Tag einen freien Tag hat dann nicht anzeigen
    if (client.additional_information?.next_work_day_off === true) {
      return false;
    }

    if (data?.employee_sick_next_work_day) {
      return false
    }
    if (data?.is_employee_client_sick_next_work_day === false) {
      return false
    }
    if (data?.stand_in_for_client_next_work_day !== undefined) {
      return false
    }

    if (
        client.additional_information?.employee_sick_next_work_day === true &&
        (
            client.additional_information?.stand_in_next_work_day === undefined ||
            client.additional_information?.stand_in_next_work_day === 0
        )
    ) {
      return true
    }

    return false

  })()

  return (
    <Modal show={show} onHide={onHide} fullscreen="md-down" size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {client.first_name} {client.last_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Persönliche Daten */}
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
          Persönliche Daten
        </h5>
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex flex-wrap gap-3">
              <div>
                <h6>Name</h6>
                <p>{client.first_name} {client.last_name}</p>
              </div>
              {client.birthday && (
                <div>
                  <h6>
                    <FontAwesomeIcon icon={faBirthdayCake} className="me-2 text-muted" />
                    Geburtstag
                  </h6>
                  <p>{format(new Date(client.birthday), 'dd.MM.yyyy')}</p>
                </div>
              )}
              {client.school_class && (
                <div>
                  <h6>
                    <FontAwesomeIcon icon={faSchool} className="me-2 text-muted" />
                    Klasse
                  </h6>
                  <p>{client.school_class}</p>
                </div>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Zeiten - wichtige Information direkt oben platzieren */}
        <Card className="mb-4 bg-light">
          <Card.Body>
            <h5 className="mb-3">
              <FontAwesomeIcon icon={faClock} className="me-2 text-primary" />
              Zeiten für heute
            </h5>

            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {timeEvents.map((event, index) => (
                <div key={index} className="text-center p-2 bg-white rounded shadow-sm" style={{minWidth: '140px'}}>
                  <h4 className="mb-1 text-primary">{event.time}</h4>
                  <div className="text-muted">{event.label}</div>
                </div>
              ))}

              {timeEvents.length === 0 && (
                <p className="text-center w-100">Keine Zeitangaben verfügbar</p>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Schule - zweithöchste Priorität */}
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faSchool} className="me-2 text-primary" />
          Schule
        </h5>
        {client.school ? (
          <Card className="mb-4">
            <Card.Body>
              <h5>{client.school.name || 'Nicht angegeben'}</h5>
              <p className="mb-0">
                {client.school.street || ''}{client.school.street ? ',' : ''} {client.school.zip_code || ''} {client.school.city || ''}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Alert variant="info" className="mb-4">
            Keine Schulinformationen verfügbar
          </Alert>
        )}

        {/* Kontakte - dritthöchste Priorität */}
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
          Kontakte
        </h5>
        {client.contacts && client.contacts.length > 0 ? (
          <ListGroup className="mb-4">
            {client.contacts.map((contact, index) => (
              <ListGroup.Item key={index} className="p-3">
                <div className="d-flex justify-content-between">
                  <div>
                    <h6>
                      {contact.first_name} {contact.last_name}
                      <Badge bg="secondary" className="ms-2">{contact.role}</Badge>
                    </h6>
                    {contact.telefon_one && (
                      <div className="mb-1">
                        <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                        {contact.telefon_one}
                      </div>
                    )}
                    {contact.telefon_two && (
                      <div>
                        <FontAwesomeIcon icon={faPhone} className="me-2 text-muted" />
                        {contact.telefon_two}
                      </div>
                    )}
                  </div>
                  {contact.telefon_one && (
                    <a
                      href={`tel:${contact.telefon_one}`}
                      className="btn btn-primary align-self-center"
                      style={{height: 'fit-content'}}
                    >
                      <FontAwesomeIcon icon={faPhone} className="me-2" />
                      Anrufen
                    </a>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="info" className="mb-4">
            Keine Kontaktinformationen verfügbar
          </Alert>
        )}

        {/* Adresse - niedrigste Priorität */}
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" />
          Adresse des Klienten
        </h5>
        {client.address ? (
          <Card>
            <Card.Body>
              <p className="mb-0">
                {client.address.street + ", " || ''} {client.address.zip_code || ''} {client.address.city || ''}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Alert variant="info">
            Keine Adressinformationen verfügbar (Adresse nur bei Schulwegbegleitung verfügbar)
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column-reverse flex-md-row justify-content-md-between align-items-stretch">
        <Button variant="outline-secondary" onClick={onHide} className="mt-2 mt-md-0 py-2">
          {showConfirmButton ? 'Abbrechen' : 'Schließen'}
        </Button>
        {showConfirmButton && onConfirm && (
          <div className="d-flex flex-column flex-md-row">
            {enableStandInTodayButton && (
                <Button
                    variant="success"
                    onClick={() => onConfirm?.(false)}
                    disabled={!enableStandInTodayButton || loading}
                    className="w-100 py-2"
                >
                  {loading ? (
                      <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Wird bestätigt...
                      </>
                  ) : (
                      <>
                        <FontAwesomeIcon icon={faUserNurse} className="me-2" />
                        Vertretung heute übernehmen
                      </>
                  )}
                </Button>
            )}
            {enableStandInNextWorkDayButton && (
                <Button
                    variant="outline-success"
                    onClick={() => onConfirm?.(true)}
                    disabled={!enableStandInNextWorkDayButton || loading}
                    className="w-100 py-2 ms-md-2 mt-2 mt-md-0"
                >
                  {loading ? (
                      <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Wird bestätigt...
                      </>
                  ) : (
                      <>
                        <FontAwesomeIcon icon={faUserNurse} className="me-2" />
                        Vertretung für {data ? format(data.next_work_day, "EEE d. MMMM", {locale: de}): 'morgen' } übernehmen
                      </>
                  )}
                </Button>
            )}
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ClientDetailModal;
